// frontend/src/components/order/OrderDetails.js - Updated without discounts/coupons

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Package,
  MapPin,
  Phone,
  Calendar,
  CreditCard,
  Truck,
  User,
  RefreshCw,
  CheckCircle,
  Clock,
  Receipt,
  DollarSign,
  ShoppingBag,
  AlertCircle,
  Info,
  CalendarClock,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';

// Enhanced format function for displaying quantity based on price type
const formatQuantityDisplay = (item) => {
  if (item.priceType === 'perKg') {
    return `${item.quantity} kg`;
  } else if (item.priceType === 'perDozen') {
    return `${item.quantity} dozen (${item.quantity * 12} eggs)`;
  } else if (item.priceType === 'perPack') {
    return `${item.quantity} pack${item.quantity > 1 ? 's' : ''}`;
  } else if (item.priceType === 'fixed' && item.selectedWeight) {
    return `${item.quantity} × ${item.selectedWeight}`;
  }
  return `Quantity: ${item.quantity}`;
};

// Updated OrderItemWeight component
const OrderItemWeight = ({ item }) => {
  return (
    <div className="text-sm text-gray-600">
      {formatQuantityDisplay(item)}
    </div>
  );
};

const OrderDetails = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      const { data } = await axios.get(`/api/v1/order/${id}`);
      if (data.success) {
        setOrder(data.order);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching order details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    navigate('/orders');
  };

  const getPaymentStatus = () => {
    if (!order?.paymentInfo) return '';
    if (order.paymentInfo.paymentMethod === 'COD') {
      return 'Cash on Delivery';
    }
    return order.paymentInfo.status === 'succeeded' ? 'Paid' : 'Payment Pending';
  };

  // Calculate item price without discounts
  const calculateItemPrice = (item) => {
    let basePrice = item.price;
    if (item.priceType === 'perDozen') {
      basePrice = item.dozenPrice || item.price;
    } else if (item.priceType === 'perPack') {
      basePrice = item.packPrice || item.price;
    }
    return basePrice;
  };

  const getAreaName = (pincode) => {
    const areas = {
      '560098': 'Rajarajeshwari Nagar',
      '560060': 'Kengeri/Viswaneedam',
      '560056': 'Bangalore Viswavidalaya',
      '562130': 'Seegehalli'
    };
    return areas[pincode] || pincode;
  };

  // Component to show delivery timing information
  const DeliveryTimingInfo = () => {
    // Don't show for delivered or cancelled orders
    if (!order || order.orderStatus === 'Order Delivered' || order.orderStatus === 'Cancelled') {
      return null;
    }
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" />
          <p className="text-sm text-blue-700">
            Your order will be delivered within 1 hour
          </p>
        </div>
      </div>
    );
  };

  const OrderStatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
      switch(status) {
        case 'Order Delivered':
          return { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle };
        case 'Order Out for Delivery':
          return { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Truck };
        case 'Cancelled':
          return { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle };
        case 'Order Packed':
          return { bg: 'bg-blue-100', text: 'text-blue-800', icon: Package };
        default:
          return { bg: 'bg-gray-100', text: 'text-gray-800', icon: Package };
      }
    };

    const config = getStatusConfig(status);
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatPrice = (item) => {
    let basePrice = item.price;
    let suffix = '';
    
    // Handle different price types
    if (item.priceType === 'perKg') {
      suffix = '/kg';
    } else if (item.priceType === 'perDozen') {
      basePrice = item.dozenPrice || item.price;
      suffix = '/dozen';
    } else if (item.priceType === 'perPack') {
      basePrice = item.packPrice || item.price;
      suffix = '/pack';
    }
    
    return {
      price: basePrice,
      suffix,
      formattedPrice: `₹${basePrice.toFixed(2)}${suffix}`
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Order not found</h3>
          <p className="mt-1 text-sm text-gray-500">The requested order could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-3 sm:py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className="inline-flex items-center mb-4 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          <span className="font-medium text-sm">Back</span>
        </button>
        
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Order Header */}
          <div className="bg-[#dc2626] px-3 sm:px-4 py-4 sm:py-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  <div>
                    <h1 className="text-base sm:text-lg font-bold text-white font-['Crimson_Text']">
                      Order #{order.orderId}
                    </h1>
                    <div className="flex items-center gap-1 text-red-100 mt-1">
                      <Calendar className="h-3 w-3" />
                      <span className="text-xs">{new Date(order.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-end gap-2">
                <OrderStatusBadge status={order.orderStatus} />
                <div className="px-3 py-1 bg-white rounded-lg shadow-sm">
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4 text-[#dc2626]" />
                      <span className="text-sm font-bold text-[#dc2626]">
                        ₹{order.totalPrice}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
            {/* Delivery Timing Information */}
            <DeliveryTimingInfo />

            {/* Order Items */}
            <div className="space-y-3">
              <h2 className="text-sm sm:text-base font-bold text-[#dc2626] flex items-center gap-1 font-['Crimson_Text']">
                <ShoppingBag className="h-4 w-4" />
                Order Items
              </h2>
              <div className="space-y-3">
                {order.orderItems.map((item, index) => {
                  const { price, suffix, formattedPrice } = formatPrice(item);
                  return (
                    <div key={index} className="flex items-center border border-gray-100 p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg"
                      />
                      <div className="ml-2 sm:ml-3 flex-1">
                        <h4 className="font-medium text-xs sm:text-sm text-gray-900">{item.name}</h4>
                        <div className="text-xs text-gray-600">
                          {formatQuantityDisplay(item)}
                        </div>
                        <div className="mt-1">
                          <span className="text-xs text-[#dc2626] font-medium">
                            {formattedPrice}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            × {item.quantity}
                          </span>
                        </div>
                        <p className="text-xs text-[#dc2626] font-medium mt-1">
                          Subtotal: ₹{(price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              {/* Payment Information */}
              <div>
                <h2 className="text-sm sm:text-base font-bold text-[#dc2626] mb-2 sm:mb-3 flex items-center gap-1 font-['Crimson_Text']">
                  <CreditCard className="h-4 w-4" />
                  Payment Information
                </h2>
                <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Payment Method:</span>
                      <span className="font-medium">{order.paymentInfo.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Payment Status:</span>
                      <span className={`font-medium ${
                        order.paymentInfo.status === 'succeeded'
                          ? 'text-green-600'
                          : 'text-yellow-600'
                      }`}>
                        {getPaymentStatus()}
                      </span>
                    </div>
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Items Total:</span>
                        <span className="font-medium">₹{order.itemsPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Delivery Charge:</span>
                        <span className="font-medium">₹{order.shippingPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="font-medium text-xs">Total Amount:</span>
                        <span className="font-bold text-sm text-[#dc2626]">₹{order.totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {/* Shipping Address */}
              <div>
                <h2 className="text-sm sm:text-base font-bold text-[#dc2626] mb-2 sm:mb-3 flex items-center gap-1 font-['Crimson_Text']">
                  <MapPin className="h-4 w-4" />
                  Delivery Address
                </h2>
                <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-xs text-gray-900">{order.shippingInfo.address}</p>
                        <p className="text-xs text-gray-500 mt-1">{getAreaName(order.shippingInfo.postalCode)}</p>
                        <p className="text-xs text-gray-500">PIN: {order.shippingInfo.postalCode}</p>
                        {order.shippingInfo.areaName && (
                          <p className="text-xs text-gray-500 mt-1">
                            Area: {order.shippingInfo.areaName}
                            {order.shippingInfo.hubPlace && ` | Hub: ${order.shippingInfo.hubPlace}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2 pt-3 border-t">
                      <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Contact Number</p>
                        <p className="font-medium text-xs text-gray-900">{order.shippingInfo.phoneno}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Date/Time Information */}
            <div>
              <h2 className="text-sm sm:text-base font-bold text-[#dc2626] mb-2 sm:mb-3 flex items-center gap-1 font-['Crimson_Text']">
                <CalendarClock className="h-4 w-4" />
                Order Information
              </h2>
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Order Date</p>
                      <p className="font-medium text-xs text-gray-900">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Order Time</p>
                      <p className="font-medium text-xs text-gray-900">
                        {new Date(order.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  {order.deliveredAt && (
                    <div className="flex items-start gap-2 col-span-1 sm:col-span-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Delivered At</p>
                        <p className="font-medium text-xs text-gray-900">
                          {new Date(order.deliveredAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Delivery Staff Information (if assigned) */}
            {order.deliveryStaff && (
              <div>
                <h2 className="text-sm sm:text-base font-bold text-[#dc2626] mb-2 sm:mb-3 flex items-center gap-1 font-['Crimson_Text']">
                  <Truck className="h-4 w-4" />
                  Delivery Staff
                </h2>
                <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <User className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Staff Name</p>
                        <p className="font-medium text-xs text-gray-900">{order.deliveryStaffName}</p>
                      </div>
                    </div>
                    {order.deliveryStaff.phone && (
                      <div className="flex items-start gap-2">
                        <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Contact Number</p>
                          <p className="font-medium text-xs text-gray-900">{order.deliveryStaff.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Delivery Charge Information */}
            <div>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-indigo-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-xs text-indigo-700">Delivery Information</p>
                    <p className="text-xs text-indigo-600">
                      Fixed delivery charge: ₹50 for all orders | Estimated delivery: Within 1 hour
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Support Contact Information */}
            <div>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-indigo-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-xs text-indigo-700">Need Help?</p>
                    <p className="text-xs text-indigo-600">
                      Contact our support team at +91 6364740404 or email us at support@meatbuddy.in
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;